import * as React from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '@/api/client';

type NotificationItem = {
  id: number;
  title: string;
  content: string;
  type: string;
  is_read: number;
  ref_id: string;
  created_at: string;
};

// 便捷类型：getNotifications 返回结构
type NotifResp = { ok: boolean; items: NotificationItem[]; unread: number };

export function NotificationBell() {
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [unread, setUnread] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<NotificationItem | null>(null);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const load = React.useCallback(async () => {
    try {
      const data = await getNotifications(50) as unknown as NotifResp;
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  // 定时轮询未读数（小喇叭圆点保持新鲜）
  React.useEffect(() => {
    const poll = () => {
      import('@/api/client').then((m) =>
        m.getUnreadCount().then((d: any) => setUnread(d?.unread || 0)).catch(() => {})
      );
    };
    poll();
    const t = setInterval(poll, 60000); // 每 60s 刷新一次角标
    return () => clearInterval(t);
  }, []);

  // 点击外部关闭小弹窗
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = React.useCallback(async () => {
    setOpen((o) => !o);
    if (!open) {
      setLoading(true);
      await load();
      setLoading(false);
    }
  }, [open, load]);

  const handleRead = React.useCallback(async (n: NotificationItem) => {
    setSelected(n);
    if (!n.is_read) {
      try {
        await markNotificationRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
  }, []);

  const handleReadAll = React.useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, is_read: 1 })));
      setUnread(0);
    } catch { /* ignore */ }
  }, []);

  return (
    <div className="relative" ref={rootRef}>
      {/* 小喇叭按钮 + 黄色未读圆点 */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggle}
        title="通知"
        className="relative"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400" />
        )}
      </Button>

      {/* 通知小弹窗（侧边栏底部场景：向上展开，左对齐） */}
      {open && (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-80 overflow-hidden rounded-xl border bg-background shadow-xl">
          {/* 面板头 */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">通知</span>
              {unread > 0 && (
                <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-xs font-medium text-amber-600">
                  {unread} 条未读
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleReadAll} title="全部已读">
                <CheckCheck className="h-3.5 w-3.5" /> 全部已读
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* 通知列表 */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">暂无通知</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleRead(n)}
                  className={`block w-full border-b px-4 py-3 text-left transition-colors last:border-0 hover:bg-accent/40 ${
                    n.is_read ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />}
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-medium ${n.is_read ? '' : 'font-semibold'}`}>
                        {n.title || '通知'}
                      </div>
                      {!selected || selected.id !== n.id ? (
                        <div className="mt-0.5 line-clamp-2 text-xs">{n.content}</div>
                      ) : (
                        <div className="mt-1 whitespace-pre-wrap text-xs">{n.content}</div>
                      )}
                      <div className="mt-1 text-[11px] text-muted-foreground/70">
                        {n.created_at ? new Date(n.created_at).toLocaleString('zh-CN') : ''}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
